import { GitHub } from '@/components/icons/Github';
import {
  extractGitHubRepositoryReference,
  prepareContent
} from '@/lib/message';
import { memo } from 'react';

import type { IMessageElement, IStep } from '@chainlit/react-client';

import { CURSOR_PLACEHOLDER } from '@/components/BlinkingCursor';
import { Markdown } from '@/components/Markdown';
import { isStudentMessageStep } from '@/lib/stepVisibility';

import { CitationChips } from './CitationChips';
import { InlinedElements } from './InlinedElements';

export interface Props {
  elements: IMessageElement[];
  message: IStep;
  allowHtml?: boolean;
  latex?: boolean;
}

const MessageContent = memo(
  ({ message, elements, allowHtml, latex }: Props) => {
    // Student chat is a transcript, not an agent debugger. Tool/run/LLM steps
    // are retained in Chainlit and remain available in the admin audit view,
    // but their arguments and results must never enter the student renderer.
    if (!isStudentMessageStep(message)) return null;

    const outputContent =
      message.streaming && message.output
        ? message.output + CURSOR_PLACEHOLDER
        : message.output;
    const githubReference =
      message.type === 'user_message'
        ? extractGitHubRepositoryReference(outputContent)
        : {
            displayContent: outputContent,
            hasRepository: false,
            repositoryUrl: null
          };

    const {
      preparedContent: output,
      inlinedElements: outputInlinedElements,
      refElements: outputRefElements
    } = prepareContent({
      elements,
      id: message.id,
      content: githubReference.displayContent,
      language: message.language
    });

    const outputMarkdown = (
      <Markdown
        allowHtml={allowHtml}
        latex={latex}
        refElements={outputRefElements}
      >
        {output}
      </Markdown>
    );

    return (
      <div className="message-content w-full flex flex-col gap-2">
        {githubReference.repositoryUrl ? (
          <a
            aria-label="Open GitHub repository"
            className="inline-grid h-10 w-10 place-items-center self-end rounded-xl border border-current/20 bg-black/10 text-current shadow-sm"
            data-testid="github-repository-reference"
            href={githubReference.repositoryUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            <GitHub className="!size-5 fill-current" />
          </a>
        ) : null}
        {output ? outputMarkdown : null}
        <CitationChips
          elements={elements}
          messageId={message.id}
          messageContent={message.output}
        />
        <InlinedElements elements={outputInlinedElements} />
      </div>
    );
  }
);

export { MessageContent };
